/**
 * Work week and Week side by side — `docs/activity.md` §6.4.1 (Board 4 A):
 * a row per person, a cell per day — each stop as **time · city**, each block
 * hatched with its time and title, *Off* on a day they do not work, and the
 * day's driving under it. Four items, then *+n more*. Clicking a cell opens
 * Day for that date with the same people.
 */
import { KIND_STYLES, drivingWords, type CalendarAnswer, type CalendarItem } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { useActivity } from '../activity-context.js'
import { Face } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { DayHead } from './day-head.js'
import { Forecast } from './blocks.js'
import { PAST_TINT, clock } from './look.js'
import { useNowMinutes } from './now.js'

const MAX_ITEMS = 4

function CellItem({ item, faded }: { item: CalendarItem; faded: boolean }) {
  if (item.kind === 'block') {
    return (
      <div className="flex h-[22px] items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-[5px] px-1.5 text-[11px] font-semibold text-text-muted"
        style={{ background: 'repeating-linear-gradient(-45deg,rgb(244 243 239),rgb(244 243 239) 5px,rgb(236 235 230) 5px,rgb(236 235 230) 10px)', opacity: faded ? 0.72 : 1 }}>
        {clock(item.start)} {item.allDay ? 'Time off' : item.title}
      </div>
    )
  }
  const colour = item.kind === 'appointment' ? KIND_STYLES.site_visit.fg
    : item.kind === 'meeting' ? KIND_STYLES.meeting.fg : KIND_STYLES.task.fg
  const where = item.place?.city ?? (item.kind === 'task' ? item.title : item.title)
  return (
    <div className="flex h-[22px] items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-[5px] border border-line-faint bg-surface px-1.5 text-[11.5px]"
      style={{ opacity: faded ? 0.72 : 1 }}>
      <i className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: colour }} />
      <span className="w-[34px] shrink-0 font-semibold text-text-muted">{clock(item.start)}</span>
      <b className="truncate font-semibold">{where}</b>
    </div>
  )
}

export function WeekRows({
  answer, days, today, date, onOpenDay,
}: {
  answer: CalendarAnswer
  days: string[]
  today: string
  /** The day the arrows are on, ringed in its head. */
  date?: string
  onOpenDay: (day: string) => void
}) {
  const { state } = useActivity()
  const show = state.calendar.show
  const now = useNowMinutes(answer)
  /* Stops and blocks only (§6.4.1, Board 4): a task is not where somebody is. */
  const visible = (item: CalendarItem) => (item.kind === 'block' ? show.blocks
    : item.kind === 'task' ? false : show.visits)

  return (
    /* The card fills the page (Aaron, Oct 23); the rows scroll inside it under the pinned days. */
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-xl border border-line bg-surface" data-grid="week-rows">
      <div className="sticky top-0 z-10 flex shrink-0 bg-surface">
        <div className="w-[170px] shrink-0" />
        {days.map((day) => {
          const weather = answer.dates.find((d) => d.date === day)?.weather
          return (
            <div key={day} className="flex h-[52px] min-w-0 flex-1 items-center justify-center border-l border-line-faint"
              style={{ background: day < today ? PAST_TINT : day === today ? 'rgb(var(--color-green-band-bg))' : undefined }}>
              <DayHead date={day} today={today} selected={day === date} weather={weather ? <Forecast weather={weather} /> : undefined} />
            </div>
          )
        })}
      </div>
      {answer.people.map((person) => (
        <div key={person.id} data-person-row={person.name} className="flex flex-1 shrink-0 border-t border-line-faint">
          <div className="w-[170px] shrink-0 p-3">
            <div className="flex items-center gap-[7px]">
              <Face person={person} size={26} />
              <span className="flex min-w-0 flex-col">
                <b className="truncate text-[12.5px]">{person.name}</b>
                <span className="text-[10.5px] text-text-muted">{person.role}</span>
              </span>
            </div>
          </div>
          {days.map((day) => {
            const own = person.days.find((d) => d.date === day)
            const gone = day < today
            const items = (own?.items ?? []).filter(visible).sort((a, b) => a.start - b.start)
            const shown = items.slice(0, MAX_ITEMS)
            const more = items.length - shown.length
            return (
              <button key={day} type="button" data-cell={day} onClick={() => onOpenDay(day)}
                className={cn('flex min-h-[120px] min-w-0 flex-1 flex-col gap-[3px] border-l border-line-faint p-2 text-left',
                  own && !own.working && 'bg-surface-alt')}
                style={{
                  boxShadow: gone ? `inset 0 0 0 999px ${PAST_TINT}` : undefined,
                  background: !gone && day === today ? 'rgb(var(--color-green-band-bg))' : undefined,
                }}>
                {own && !own.working ? (
                  <span className="text-[11px] font-semibold text-text-faint">Off</span>
                ) : (
                  <>
                    {shown.map((item) => (
                      /* Today, what has already started steps back with the gone days (Board 4 A: Thursday's 8:00–9:00). */
                      <CellItem key={item.key} item={item} faded={gone || (day === today && item.start < now)} />
                    ))}
                    {more > 0 && <span className="text-[11px] font-semibold text-text-muted">+{more} more</span>}
                    {show.drives && own && own.driving > 0 && (
                      <span className="mt-0.5 flex items-center gap-1 text-[10.5px] font-semibold text-text-faint">
                        <Glyph name="car" size={11} />{drivingWords(own.driving)}
                      </span>
                    )}
                  </>
                )}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
